/**
 * @fileoverview Supervision des intégrations BO-05.
 *
 * Vue UI des connecteurs critiques : STI et guichet de paiement. Chaque
 * connecteur expose une frise horaire des 24 dernières heures
 * (statut par heure) façon « status page », avec une pastille d'état animée —
 * visualisation rapatriée du tableau de bord pour vivre là où elle porte le
 * plus de sens : la page dédiée à la santé des intégrations.
 */

import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  Amount,
  Button,
  Card,
  CardContent,
  PageHeader,
  SectionHeader,
  StatusBadge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@gnpc/ui';
import { INTEGRATIONS, formatDateTime, type IntegrationHealth } from '../data/backoffice.ts';

/** État d'une tranche horaire de la timeline d'un connecteur. */
type IntegrationSliceStatus = 'UP' | 'DEGRADED' | 'DOWN';

const INTEGRATION_TIMELINE_HOURS = 24;

const INTEGRATION_SLICE_CLASSES: Record<IntegrationSliceStatus, string> = {
  UP: 'bg-integration-up',
  DEGRADED: 'bg-integration-degraded',
  DOWN: 'bg-integration-down',
};

const INTEGRATION_SLICE_LABELS: Record<IntegrationSliceStatus, string> = {
  UP: 'Opérationnel',
  DEGRADED: 'Dégradé',
  DOWN: 'Indisponible',
};

/**
 * Timeline mockée des 24 dernières heures d'un connecteur, DÉTERMINISTE
 * (seed dérivé de l'id) et cohérente avec son statut courant : un
 * connecteur DEGRADED montre une dégradation récente, un UP un historique
 * quasi propre. Au branchement API : remplacé par l'historique de sondes.
 */
function buildIntegrationTimeline(integration: IntegrationHealth): IntegrationSliceStatus[] {
  const seed = Array.from(integration.id).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );

  return Array.from({ length: INTEGRATION_TIMELINE_HOURS }, (_, hour) => {
    if (integration.status === 'DOWN') {
      if (hour >= INTEGRATION_TIMELINE_HOURS - 3) return 'DOWN';
      if (hour >= INTEGRATION_TIMELINE_HOURS - 6) return 'DEGRADED';
      return 'UP';
    }
    if (integration.status === 'DEGRADED') {
      // Dégradation en cours : les dernières heures sont oranges, avec un
      // creux rouge au moment de la bascule.
      if (hour >= INTEGRATION_TIMELINE_HOURS - 4) return 'DEGRADED';
      if (hour === INTEGRATION_TIMELINE_HOURS - 6) return 'DOWN';
      return Math.abs(Math.sin((hour + seed) * 2.3)) > 0.9 ? 'DEGRADED' : 'UP';
    }
    // UP : historique propre, une heure dégradée ancienne si le connecteur
    // a un taux d'erreurs résiduel.
    return integration.errorRate > 0 && hour === seed % 8 ? 'DEGRADED' : 'UP';
  });
}

/** Libellé d'une tranche horaire — ex : « 13 juil., 10:00 ». */
function formatIntegrationSlot(lastCheckAt: string, hourIndex: number): string {
  const reference = new Date(lastCheckAt);
  reference.setUTCMinutes(0, 0, 0);
  const slot = new Date(
    reference.getTime() - (INTEGRATION_TIMELINE_HOURS - 1 - hourIndex) * 3_600_000,
  );
  return slot.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/**
 * Disponibilité affichée d'un connecteur sur la frise 24 h.
 *
 * Convention status page : une heure dégradée compte pour moitié dans le
 * taux, une heure indisponible pour rien.
 */
function timelineAvailability(timeline: readonly IntegrationSliceStatus[]): number {
  const upSlices = timeline.filter((slice) => slice === 'UP').length;
  const degradedSlices = timeline.filter((slice) => slice === 'DEGRADED').length;
  return ((upSlices + degradedSlices * 0.5) / timeline.length) * 100;
}

/**
 * Dot d'état d'un connecteur — halo animé (`animate-ping`) tant que le
 * connecteur répond (UP / DEGRADED), fixe quand il est DOWN. L'animation
 * est neutralisée globalement par `prefers-reduced-motion`.
 */
function IntegrationStatusDot({
  status,
}: {
  status: IntegrationHealth['status'];
}): JSX.Element {
  const colorClass = INTEGRATION_SLICE_CLASSES[status];

  return (
    <span aria-hidden="true" className="relative flex size-2.5 shrink-0">
      {status !== 'DOWN' && (
        <span
          className={cn(
            'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
            colorClass,
          )}
        />
      )}
      <span className={cn('relative inline-flex size-2.5 rounded-full', colorClass)} />
    </span>
  );
}

export function IntegrationsPage(): JSX.Element {
  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Supervision"
        title="Santé des intégrations"
        description="Suivre la disponibilité des services qui conditionnent la consultation, le paiement et l'enregistrement Quipux, et leur impact financier."
      />

      <section className="grid gap-4 md:grid-cols-2">
        {INTEGRATIONS.map((integration) => {
          const timeline = buildIntegrationTimeline(integration);
          const availability = timelineAvailability(timeline);

          return (
            <Card key={integration.id}>
              <CardContent className="grid gap-4 p-5">
                <SectionHeader
                  title={
                    <span className="flex items-center gap-2">
                      <IntegrationStatusDot status={integration.status} />
                      <span className="min-w-0 truncate">{integration.label}</span>
                    </span>
                  }
                  description={integration.description}
                  action={<StatusBadge kind="integration" status={integration.status} compact />}
                />

                {/* Impact métier chiffré + drill-down pour un connecteur dégradé (C-28) :
                  * relie la dégradation aux transactions concernées et au montant
                  * immobilisé — vue orientée impact financier, pas latence brute. */}
                {integration.status !== 'UP' && integration.impactedCount ? (
                  <div className="border-danger/25 bg-danger/5 grid gap-2 rounded-lg border p-3 text-sm">
                    <div className="flex items-center gap-2 font-medium text-[#991b1b]">
                      <AlertTriangle className="size-4 shrink-0" />
                      Impact métier
                    </div>
                    <p className="text-muted-foreground">
                      {integration.impactedCount} transaction(s) impactée(s)
                      {integration.impactedAmount ? (
                        <>
                          {' · '}
                          <span className="text-quipux-navy-950 font-semibold">
                            <Amount value={integration.impactedAmount} />
                          </span>{' '}
                          immobilisés
                        </>
                      ) : null}
                    </p>
                    {integration.impactedLink ? (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="border-danger/30 justify-self-start text-[#991b1b] hover:bg-danger/10 hover:text-[#991b1b]"
                      >
                        <Link to={integration.impactedLink}>Voir les transactions impactées</Link>
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {/* Frise horaire des 24 dernières heures façon « status page » :
                  * un tiret coloré par heure (tooltip individuel), la
                  * disponibilité agrégée alignée à droite. */}
                <div className="grid gap-1.5">
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="text-muted-foreground">Disponibilité 24 h</span>
                    <span className="shrink-0 font-semibold tabular-nums text-quipux-navy-950">
                      {availability.toLocaleString('fr-FR', {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}
                      {' % dispo.'}
                    </span>
                  </div>
                  <div
                    className="flex h-5 items-stretch gap-[3px]"
                    role="img"
                    aria-label={`Disponibilité des 24 dernières heures : ${availability.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
                  >
                    {timeline.map((slice, hourIndex) => (
                      <Tooltip key={hourIndex}>
                        <TooltipTrigger asChild>
                          <span
                            className={cn('flex-1 rounded-full', INTEGRATION_SLICE_CLASSES[slice])}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p className="font-medium">
                            {formatIntegrationSlot(integration.lastCheckAt, hourIndex)}
                          </p>
                          <p className="text-background/70">{INTEGRATION_SLICE_LABELS[slice]}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </div>

                <dl className="grid gap-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Latence</dt>
                    <dd className="text-quipux-navy-950 font-medium">{integration.latencyMs} ms</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Taux d'erreur</dt>
                    <dd className="text-quipux-navy-950 font-medium">{integration.errorRate} %</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Dernier contrôle</dt>
                    <dd className="text-quipux-navy-950 text-right font-medium">
                      {formatDateTime(integration.lastCheckAt)}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          );
        })}
      </section>
    </div>
  );
}
